import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  List,
  Badge,
  Divider,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout,
  Settings,
  Person,
  Lock,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  EventNote as TimetableIcon,
  RequestPage as LeaveIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Sidebar from "../components/layout/Sidebar";
import ChangePasswordModal from "../components/common/ChangePasswordModal";
import { getAvatarInitial } from "../utils/avatarUtils";
import { logout, setUser } from "../store/slices/authSlice";
import { fetchNotifications, fetchAllNotifications } from "../store/slices/notificationSlice";
import { getNotificationNavigationPath } from "../utils/notificationNavigation";
import { fetchConversations } from "../store/slices/chatSlice";
import profileService from "../services/profileService";
import { registerFcmTokenWithBackend } from "../services/firebaseService";
import { useSocket } from "../hooks/useSocket";
import { useTokenKeepAlive } from "../hooks/useTokenKeepAlive";
import { usePermissions } from "../hooks/usePermissions";

const drawerWidth = 280;

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleNewNotification = (n) => {
    // We only update Redux via useSocket, no local toast needed
  };

  const handleNewMessage = (m) => {
    // We only update Redux via useSocket, no local toast needed
  };

  useSocket({ 
    onNewNotification: handleNewNotification,
    onNewMessage: handleNewMessage 
  });
  useTokenKeepAlive();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { hasVisiblePermission, isLoading: permissionsLoading } = usePermissions();
  const { user, consentRequired } = useSelector((state) => state.auth);
  const { selectedInstitute, isInInstituteContext, isSwitching, switchingMessage } =
    useSelector((state) => state.instituteContext);
  const rawRole = user?.role?.slug || user?.role || "student";
  const userRole = (typeof rawRole === "string" ? rawRole.toLowerCase() : rawRole) === "institute" ? "admin" : (typeof rawRole === "string" ? rawRole.toLowerCase() : rawRole);
  const isSuperAdminInContext =
    userRole === "superadmin" && isInInstituteContext;
  const isPureSuperAdminPanel =
    userRole === "superadmin" &&
    !isInInstituteContext &&
    location.pathname.startsWith("/superadmin");
  const effectiveInstitute = userRole === "superadmin" ? selectedInstitute : (selectedInstitute || user?.institute || null);
  const showInstituteInAvatar =
    !isPureSuperAdminPanel &&
    ((userRole === "superadmin" && isInInstituteContext && selectedInstitute) ||
      (userRole === "admin" && effectiveInstitute));
  const notificationsState = useSelector((state) => state.notifications);
  const systemUnreadCount = notificationsState?.unreadCount ?? 0;

  const chatConversations = useSelector((state) => state.chat.conversations || []);
  const chatUnreadCount = chatConversations.reduce((sum, conv) => sum + (conv.unread || 0), 0);
  const totalUnreadCount = systemUnreadCount + chatUnreadCount;

  const unreadChats = chatConversations
    .filter(c => (c.unread || 0) > 0)
    .map(c => ({
      id: `chat_${c.id}`,
      title: `${c.name || (c.participants && c.participants[0]?.firstName) || 'Someone'} sent a message`,
      message: c.lastMessage || 'New message received',
      isRead: false,
      createdAt: c.time || c.updatedAt || new Date().toISOString(),
      type: 'chat',
      conversationId: c.id
    }));

  const combinedNotifications = [
    ...unreadChats,
    ...(notificationsState?.notifications ?? [])
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const headerNotifications = combinedNotifications.slice(0, 5);

  const isTeacherOrHOD = ['teacher', 'hod'].includes(userRole?.toLowerCase());

  useEffect(() => {
    if (consentRequired) {
      navigate("/consent");
    }
  }, [consentRequired, navigate]);

  useEffect(() => {
    if (userRole === "superadmin") {
      dispatch(fetchAllNotifications({ limit: 5 }));
    } else {
      dispatch(fetchNotifications({ limit: 5 }));
    }
  }, [dispatch, userRole]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (hasVisiblePermission("chat.access")) {
      dispatch(fetchConversations({ silent: true }));
    }
  }, [dispatch, hasVisiblePermission, permissionsLoading]);

  // Register FCM token for push (hod, teacher, student)
  useEffect(() => {
    const role = (user?.role?.slug || user?.role || "").toLowerCase();
    if (["hod", "teacher", "student"].includes(role)) {
      registerFcmTokenWithBackend(profileService.updateFcmToken);
    }
  }, [user?.id, user?.role]);

  // Fetch teacher/HOD profile details on dashboard when role is teacher or hod
  const { data: teacherDetailsResponse } = useQuery({
    queryKey: ["dashboardTeacherDetails", user?.role?.toLowerCase()],
    queryFn: () => profileService.getTeacherUpdatedProfile(),
    enabled: isTeacherOrHOD,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // Sync fetched teacher details into Redux and localStorage so dashboard shows updated data
  useEffect(() => {
    if (!isTeacherOrHOD || !teacherDetailsResponse) return;
    const raw =
      teacherDetailsResponse?.data?.data ??
      teacherDetailsResponse?.data?.user ??
      teacherDetailsResponse?.data ??
      teacherDetailsResponse;
    if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0)
      return;
    // Normalize snake_case to camelCase if API returns snake_case
    const payload = {
      ...raw,
      firstName: raw.firstName ?? raw.first_name,
      lastName: raw.lastName ?? raw.last_name,
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      dateOfBirth: raw.dateOfBirth ?? raw.date_of_birth ?? raw.dob,
      dob: raw.dob ?? raw.date_of_birth ?? raw.dateOfBirth,
      isApproved: raw.isApproved,
      profileEditRequestStatus:
        raw.profileEditRequestStatus === "pending" ? "pending" : null,
      profileEditRequestId:
        raw.profileEditRequestStatus === "pending"
          ? raw.profileEditRequestId ?? null
          : null,
    };
    const updatedUser = { ...user, ...payload };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives, not when user changes after dispatch
  }, [isTeacherOrHOD, teacherDetailsResponse, dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
    if (userRole === "superadmin") {
      dispatch(fetchAllNotifications({ limit: 5 }));
    } else {
      dispatch(fetchNotifications({ limit: 5 }));
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith("/chat")) return "Messages";
    const titles = {
      "/dashboard": "Dashboard",
      "/profile": "Profile",
      "/profile/contact-requests": "Contact Info Requests",
      "/contact-support": "Contact support",
      "/notifications": "Notifications",
      "/student/assignments": "Assignments",
      "/student/materials": "Study Materials",
      "/student/attendance": "My Attendance",
      "/student/leaves": "Leave Applications",
      "/student/timetable": "Timetable",
      "/student/exam-results": "Exam Results",
      "/teacher/students": "Students",
      "/teacher/attendance": "Attendance Management",
      "/teacher/assignments": "Assignment Management",
      "/teacher/materials": "Material Management",
      "/teacher/exams": "Exam Management",
      "/hod/department": "Department",
      "/hod/courses": "Courses",
      "/hod/subjects": "Subjects",
      "/admin/users": "User Management",
      "/admin/departments": "Department Management",
      "/admin/roles": "Role Management",
      "/admin/import": "Import/Export",
      "/superadmin/institutes": "Institute Management",
      "/superadmin/rbac": "RBAC Management",
    };
    return titles[path] || "ERP Admin Panel";
  };


  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          {(userRole === "teacher" || userRole === "hod") && !isPureSuperAdminPanel ? (
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              {user?.instituteLogo && (
                <img
                  src={user?.instituteLogo}
                  alt="Institute Logo"
                  style={{
                    height: "32px",
                    width: "32px",
                    marginRight: "12px",
                    objectFit: "contain"
                  }}
                />
              )}
              <Typography
                variant="h6"
                component="div"
                sx={{ flexGrow: 1, fontWeight: 600 }}
              >
                {user?.institute?.name || user?.instituteName ||
                  user?.organization?.name ||
                  "logo + My Institute {Institute branding}"}{" "}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              {getPageTitle()}
            </Typography>
          )}

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton color="inherit" onClick={handleNotificationOpen}>
              <Badge badgeContent={totalUnreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton onClick={handleMenuOpen}>
              <Avatar
                sx={{ width: 36, height: 36, bgcolor: "primary.main" }}
                src={
                  showInstituteInAvatar && effectiveInstitute
                    ? (effectiveInstitute.profileImage || effectiveInstitute.avatar || undefined)
                    : (user?.profileImage || user?.avatar)
                }
              >
                {showInstituteInAvatar && effectiveInstitute && !effectiveInstitute.profileImage && !effectiveInstitute.avatar
                  ? getAvatarInitial(effectiveInstitute)
                  : getAvatarInitial(user)}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Typography variant="caption" color="primary">
                {user?.role?.name}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile}>
              <Person sx={{ mr: 1 }} /> Profile
            </MenuItem>
            {userRole !== "superadmin" && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setChangePasswordOpen(true);
                }}
              >
                <Lock sx={{ mr: 1 }} /> Change Password
              </MenuItem>
            )}
            {/* Settings - commented for now */}
            {/* <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/settings");
              }}
            >
              <Settings sx={{ mr: 1 }} /> Settings
            </MenuItem> */}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Notifications
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  handleNotificationClose();
                  navigate("/notifications");
                }}
              >
                View All
              </Typography>
            </Box>
            <Divider />
            {headerNotifications.length === 0 ? (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ maxHeight: 320, overflow: "auto", py: 0 }}>
                {headerNotifications.map((n, index) => {
                  const getIcon = () => {
                    switch (n.type) {
                      case 'chat': return <ChatIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
                      case 'leave': return <LeaveIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
                      case 'timetable': return <TimetableIcon sx={{ color: 'info.main', fontSize: 20 }} />;
                      case 'assignment': return <AssignmentIcon sx={{ color: 'success.main', fontSize: 20 }} />;
                      default: return <InfoIcon sx={{ color: 'text.secondary', fontSize: 20 }} />;
                    }
                  };

                  return (
                    <React.Fragment key={n.id}>
                      {index > 0 && <Divider variant="inset" component="li" />}
                      <MenuItem
                        dense
                        onClick={() => {
                          setNotificationAnchor(null);
                          if (n.type === "chat") {
                            navigate("/chat", { state: { conversationId: n.conversationId } });
                            return;
                          }
                          const path = getNotificationNavigationPath(n, user);
                          if (path) {
                            navigate(path);
                            return;
                          }
                          navigate("/notifications");
                        }}
                        sx={{ 
                          whiteSpace: "normal", 
                          py: 1.5,
                          px: 2,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                          {getIcon()}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={n.isRead ? 400 : 600} color="text.primary">
                            {n.title}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4,
                              mt: 0.5
                            }}
                          >
                            {n.message}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
            },
          }}
        >
          <Sidebar
            user={user}
            onClose={handleDrawerToggle}
            onOpenChangePassword={() => setChangePasswordOpen(true)}
          />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
              bgcolor: "#1a1f37",
            },
          }}
          open
        >
          <Sidebar
            user={user}
            onOpenChangePassword={() => setChangePasswordOpen(true)}
          />
        </Drawer>
      </Box>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Backdrop
        open={isSwitching}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="body1" fontWeight={500}>
          {switchingMessage || "Please wait..."}
        </Typography>
      </Backdrop>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: location.pathname.startsWith("/chat") ? 0 : 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: "64px",
          bgcolor: location.pathname.startsWith("/chat") ? "#dfe5e9" : "background.default",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;